<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { useResizeObserver } from '@vueuse/core';

const props = defineProps({
  initialActiveTab: {
    type: Number,
    default: 0,
  },
  // No celular (abaixo de md), abas mais estreitas. Com o recuo dos relatórios, os quatro chips do
  // PeriodPicker passavam da tela a 390 px, e o pressionado saía cortado ("Últimos 12 mes").
  compact: {
    type: Boolean,
    default: false,
  },
  // Abas de verdade, que trocam o painel abaixo: tablist com aria-selected, tabindex móvel, setas,
  // Home e End (o padrão de abas da APG). Sem ele, o controle segmentado de sempre (os chips do
  // PeriodPicker filtram, não trocam painel), com o aria-pressed. Cada aba pode dizer o id do
  // painel que controla em `panelId`. Os atributos de `attrs` (um `data-*` que o pai procura, por
  // exemplo) vão para o botão da aba.
  asTabs: {
    type: Boolean,
    default: false,
  },
  tabs: {
    type: Array,
    required: true,
    validator: value => {
      return value.every(
        tab =>
          typeof tab.label === 'string' &&
          (tab.count ? typeof tab.count === 'number' : true)
      );
    },
  },
});

const emit = defineEmits(['tabChanged']);

const activeTab = computed(() => props.initialActiveTab);

const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const activeElement = computed(() => tabRefs.value[activeTab.value]);

// Sem aba ativa (índice fora da lista), o fundo branco ficava sob a que deixou de ser, com o
// `aria-pressed` dizendo que nenhuma é: ele some, e volta com a próxima ativa.
const hasActiveTab = computed(
  () => activeTab.value >= 0 && activeTab.value < props.tabs.length
);

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value) return;

    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };
  });
};

useResizeObserver(activeElement, updateIndicator);

// Watch for prop/tabs changes to update indicator position
watch([() => props.initialActiveTab, () => props.tabs], updateIndicator, {
  immediate: true,
});

onMounted(() => {
  nextTick(() => {
    enableTransition.value = true;
  });
});

const selectTab = index => {
  emit('tabChanged', props.tabs[index]);
};

// As setas andam entre as abas e já selecionam (ativação automática da APG), com o foco junto.
const PASSOS = { ArrowRight: 1, ArrowLeft: -1 };
const onKeydown = (event, index) => {
  if (!props.asTabs) return;
  const total = props.tabs.length;
  let destino = null;
  if (event.key in PASSOS)
    destino = (index + PASSOS[event.key] + total) % total;
  else if (event.key === 'Home') destino = 0;
  else if (event.key === 'End') destino = total - 1;
  if (destino === null) return;
  event.preventDefault();
  event.stopPropagation();
  selectTab(destino);
  nextTick(() => tabRefs.value[destino]?.focus());
};

// Com aba ativa, só ela entra no Tab; sem aba ativa, a primeira, para o grupo não sumir do teclado.
const tabIndexDe = index => {
  if (!props.asTabs) return undefined;
  const alvo = hasActiveTab.value ? activeTab.value : 0;
  return index === alvo ? 0 : -1;
};

const showDivider = index => {
  return (
    // Show dividers after the active tab, but not after the last tab
    (index > activeTab.value && index < props.tabs.length - 1) ||
    // Show dividers before the active tab, but not immediately before it and not before the first tab
    (index < activeTab.value - 1 && index > -1)
  );
};
</script>

<template>
  <div
    :role="asTabs ? 'tablist' : undefined"
    class="relative flex items-center h-8 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 w-fit transition-all duration-200 ease-out has-[button:active]:scale-[1.01]"
  >
    <div
      class="absolute rounded-lg bg-n-solid-active shadow-sm pointer-events-none h-8 outline-1 outline outline-n-container inset-y-0"
      :class="{
        'transition-all duration-300 ease-out': enableTransition,
        'opacity-0': !hasActiveTab,
      }"
      :style="indicatorStyle"
    />

    <template v-for="(tab, index) in tabs" :key="index">
      <!-- A aba ativa se via só pela cor: o `aria-pressed` a diz também ao leitor de tela
      (UI-FILTROS-01, D8). -->
      <button
        :ref="el => (tabRefs[index] = el)"
        v-bind="tab.attrs"
        type="button"
        :role="asTabs ? 'tab' : undefined"
        :aria-pressed="asTabs ? undefined : activeTab === index"
        :aria-selected="asTabs ? activeTab === index : undefined"
        :aria-controls="asTabs ? tab.panelId : undefined"
        :tabindex="tabIndexDe(index)"
        class="relative z-10 truncate py-1.5 border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
        :class="[
          compact ? 'px-3 text-xs md:px-4 md:text-sm' : 'px-4 text-sm',
          activeTab === index
            ? 'text-n-brand scale-100'
            : 'text-n-slate-11 scale-[0.98] motion-reduce:scale-100',
        ]"
        @click="selectTab(index)"
        @keydown="onKeydown($event, index)"
      >
        <!-- O slot `tab` deixa o pai desenhar a aba (um selo colorido ao lado do rótulo, por
        exemplo); sem ele, o rótulo e a contagem de sempre. -->
        <slot
          name="tab"
          :tab="tab"
          :index="index"
          :active="activeTab === index"
        >
          {{ tab.label }} {{ tab.count ? `(${tab.count})` : '' }}
        </slot>
      </button>
      <div
        v-if="index < tabs.length - 1"
        class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out"
        :class="
          showDivider(index)
            ? 'bg-n-strong'
            : 'bg-transparent dark:bg-transparent'
        "
      />
    </template>
  </div>
</template>
